import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { _className, ComponentSync, TS_Checkbox } from '@nu-art/thunderstorm-frontend/index';
import { Digraph, Edge, Node, Subgraph, toDot } from 'ts-graphviz';
import { DBDef_Pathway } from '@app/pathway-shared';
import { _values, capitalizeFirstLetter, compare, debounce, exists, isPromise, MUSTNeverHappenException, voidFunction } from '@nu-art/ts-common';
import './Pathway_GraphView.scss';
import { graphviz } from 'd3-graphviz';
import { dispatch_PathwayStateSelected, dispatch_PathwayTransitionSelected } from '../Page_PathwayEditor/dispatchers.js';
import { ModuleFE_PathwayState, ModuleFE_PathwayTransition } from '../../_entity.js';
import { getGraphLoader, IDGeneratorPrefix_State, IDGeneratorPrefix_Transition, IDGeneratorPrefix_TransitionPoint, IDGeneratorSuffix_End, IDGeneratorSuffix_Start, updateOnSync } from './util.js';
import { Component_FocusedEntityRef } from '@nu-art/ts-focused-object-frontend/components/Component_FocusedEntityRef';
import { select, selectAll } from 'd3';
const newItemId = '##NEW##';
const RectMaxWidth = 200;
export class Pathway_GraphView extends ComponentSync {
    // ######################## Statics ########################
    /**
     * in order to be supported in advisor we need to either use modules from regular km or advisor alternatives to
     * be passed as props by context
     */
    static defaultProps = {
        pathwayTransitionModule: ModuleFE_PathwayTransition,
        pathwayStateModule: ModuleFE_PathwayState,
    };
    // ######################## Properties ########################
    graphContainerRef = React.createRef();
    nodeClass = 'pathway-graph-view__pathway-state';
    transitionNodeClass = 'pathway-graph-view__transition-node';
    edgeClass = 'pathway-graph-view__pathway-transition';
    measurementCanvas;
    deriveDebounce;
    // ######################## Lifecycle ########################
    __onPathwayTransitionUpdated(...params) {
        if (!this.state.pathwayId)
            return;
        updateOnSync(this.state.pathwayId, params, 'pathwayIds', () => this.reDeriveState(), () => this.reDeriveState());
    }
    __onPathwayUpdated(...params) {
        if (!this.state.pathwayId)
            return;
        updateOnSync(this.state.pathwayId, params, '_id', () => this.reDeriveState(), voidFunction);
    }
    __onPathwayStateUpdated(...params) {
        if (!this.state.pathwayId)
            return;
        updateOnSync(this.state.pathwayId, params, 'pathwayIds', () => this.reDeriveState(), () => this.reDeriveState());
    }
    __onPathwayStateRealtimeChanges = () => {
        this.forceUpdate();
    };
    deriveStateFromProps(nextProps, state) {
        this.deriveDebounce = this.deriveDebounce ?? debounce(this.deriveAction.bind(this), 200, 200);
        this.deriveDebounce(nextProps, state);
        return state;
    }
    deriveAction(nextProps, state) {
        state = { ...state };
        state.pathwayId = nextProps.pathwayId;
        state.editablePathwayState = nextProps.editablePathwayState;
        state.editablePathwayTransition = nextProps.editablePathwayTransition;
        state.pathwayStates = nextProps.pathwayStateModule.cache.filter(ps => ps.pathwayIds.includes(nextProps.pathwayId));
        state.pathwayTransitions = nextProps.pathwayTransitionModule.cache.filter(pt => pt.pathwayIds.includes(nextProps.pathwayId));
        state.focusedStates = { ...nextProps.focusedStates };
        state.focusedTransitions = { ...nextProps.focusedTransitions };
        state.transitionMidPoints ??= true;
        if (!this.measurementCanvas) {
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            context.font = '12px Arial';
            this.measurementCanvas = context;
        }
        this.setState(state);
    }
    componentDidMount() {
        if (!this.graphContainerRef.current) {
            this.logWarning('Finished mounting but no ref to container');
            return;
        }
        this.renderGraph();
        this.deriveAction(this.props, this.state);
    }
    componentDidUpdate(prevProps, prevState, snapshot) {
        if (!this.graphContainerRef.current) {
            this.logWarning('Somehow lost container ref between renders');
            return;
        }
        this.renderGraph();
        this.updateTransitionsParams(this.state);
        this.updateStateParams(this.state);
    }
    shouldComponentUpdate(nextProps, nextState, nextContext) {
        //First render, of course it should
        if (!this.state)
            return true;
        //Pathway Changed, so graph needs re-rendering
        if (this.state.pathwayId !== nextState.pathwayId)
            return true;
        // if there's a change in the focused transitions update their classes but don't break the function
        if (this.state.focusedTransitions || !compare(this.state.focusedTransitions, nextState.focusedTransitions))
            this.updateTransitionsParams(nextState);
        if (this.state.focusedStates || !compare(this.state.focusedStates, nextState.focusedStates))
            this.updateStateParams(nextState);
        if (!this.state.loadingStateId && !nextState.loadingStateId)
            this.removeLoader();
        if (!this.state.loadingStateId && nextState.loadingStateId)
            this.injectLoader(nextState.loadingStateId);
        //Transition render method changed, needs re-render
        if (this.state.transitionMidPoints !== nextState.transitionMidPoints)
            return true;
        //State have changes
        if (!compare(this.generateItemsUpdatedMap(nextState.pathwayStates), this.generateItemsUpdatedMap(this.state.pathwayStates)))
            return true;
        //Transitions have changes
        if (!compare(this.generateItemsUpdatedMap(nextState.pathwayTransitions), this.generateItemsUpdatedMap(this.state.pathwayTransitions)))
            return true;
        let shouldUpdate = false;
        //Selected pathway state has changed
        if (!compare(this.state.editablePathwayState?.item, nextState.editablePathwayState?.item)) {
            const lastPSId = this.state.editablePathwayState?.item._id;
            const nextPSId = nextState.editablePathwayState?.item._id;
            const lastPSWasNew = exists(this.state.editablePathwayState) && !lastPSId;
            const nextPSIsNew = exists(nextState.editablePathwayState) && !nextPSId;
            //If the last or next pathway state are new the graph needs to re-render
            const lastSelectedStateExists = this.props.pathwayStateModule.cache.unique(nextState.editablePathwayState?.item._id);
            if (lastPSWasNew || nextPSIsNew || !lastSelectedStateExists)
                shouldUpdate = true;
            //Clear styling on last selected pathway state
            if (lastPSId)
                this.setSelectedById(this.generateId(IDGeneratorPrefix_State, lastPSId), 'remove');
            //Add styling on new selected state
            if (nextPSId)
                this.setSelectedById(this.generateId(IDGeneratorPrefix_State, nextPSId), 'add');
        }
        //Selected pathway transition has changed
        if (!compare(this.state.editablePathwayTransition?.item, nextState.editablePathwayTransition?.item)) {
            const lastPTId = this.state.editablePathwayTransition?.item._id;
            const nextPTId = nextState.editablePathwayTransition?.item._id;
            const lastPTWasNew = exists(this.state.editablePathwayTransition) && !lastPTId;
            const nextPTIsNew = exists(nextState.editablePathwayTransition) && !nextPTId;
            //If the last or next pathway transition are new the graph needs to re-render
            const lastSelectedTransitionExists = this.props.pathwayTransitionModule.cache.unique(nextState.editablePathwayTransition?.item._id);
            if (lastPTWasNew || nextPTIsNew || !lastSelectedTransitionExists)
                shouldUpdate = true;
            //Clear styling on last selected pathway transition
            if (lastPTId && this.state.transitionMidPoints) {
                this.setSelectedById(this.generateId(IDGeneratorPrefix_Transition, lastPTId, IDGeneratorSuffix_Start), 'remove');
                this.setSelectedById(this.generateId(IDGeneratorPrefix_Transition, lastPTId, IDGeneratorSuffix_End), 'remove');
            }
            //Add styling on new selected transition
            if (nextPTId) {
                this.setSelectedById(this.generateId(IDGeneratorPrefix_Transition, nextPTId, IDGeneratorSuffix_Start), 'add');
                this.setSelectedById(this.generateId(IDGeneratorPrefix_Transition, nextPTId, IDGeneratorSuffix_End), 'add');
            }
        }
        return shouldUpdate;
    }
    // ######################## Logic - Graph Manipulation ########################
    setSelectedById = (id, action) => {
        const element = document.getElementById(id);
        if (!element) {
            this.logWarning(`Could not find element with id ${id}`);
            return;
        }
        element.classList[action]('selected');
    };
    // ######################## Logic - Graph Creation ########################
    generateId = (prefix, id, suffix) => {
        let str = `${prefix}${id}`;
        if (suffix)
            str += `${suffix}`;
        return str;
    };
    generateItemsUpdatedMap = (items) => {
        return items.reduce((acc, curr) => {
            acc[curr._id] = curr.__updated;
            return acc;
        }, {});
    };
    getGraphString = () => {
        const graph = new Digraph('', { bgcolor: 'transparent' });
        const subgraph = new Subgraph('Container');
        graph.addSubgraph(subgraph);
        const stateNodes = {};
        //Crude check for single state pathway instead of passing a pathway modules for querying.
        const capitalizeStateNode = (this.state.pathwayStates.length === 1 && !this.state.pathwayTransitions.length);
        //Create State Nodes
        this.state.pathwayStates.forEach(pathwayState => {
            const pathwayStateNode = this.getPathwayStateNode(pathwayState, capitalizeStateNode);
            stateNodes[pathwayState._id] = pathwayStateNode;
            subgraph.addNode(pathwayStateNode);
        });
        //Create Transitions
        this.state.pathwayTransitions.forEach(pathwayTransition => {
            const midNode = this.getTransitionNode(pathwayTransition);
            const pathwayTransitionEdges = this.getPathwayTransitionNode(pathwayTransition, stateNodes, midNode);
            if (!pathwayTransitionEdges)
                return;
            if (this.state.transitionMidPoints) { //If rendering with mid points
                subgraph.addNode(midNode);
                subgraph.addEdge(pathwayTransitionEdges[0]);
                subgraph.addEdge(pathwayTransitionEdges[1]);
            }
            else {
                subgraph.addEdge(pathwayTransitionEdges[0]);
            }
        });
        //Render the selected pathwayState if we didn't get it from the cache (new state)
        const selectedState = this.state.editablePathwayState?.item;
        if (selectedState) {
            const existing = this.state.pathwayStates.find(ps => ps._id === selectedState._id);
            if (!existing) {
                const newStateNode = this.getPathwayStateNode(selectedState, capitalizeStateNode);
                subgraph.addNode(newStateNode);
            }
        }
        //Render the selected pathwayTransition if we didn't get it from the cache (new transition)
        const selectedTransition = this.state.editablePathwayTransition?.item;
        if (selectedTransition && (!selectedTransition?._id || !this.state.pathwayTransitions.find(pt => pt._id === selectedTransition._id))) {
            const midNode = this.getTransitionNode(selectedTransition);
            const pathwayTransitionEdges = this.getPathwayTransitionNode(selectedTransition, stateNodes, midNode);
            if (pathwayTransitionEdges) {
                if (this.state.transitionMidPoints) {
                    subgraph.addNode(midNode);
                    subgraph.addEdge(pathwayTransitionEdges[0]);
                    subgraph.addEdge(pathwayTransitionEdges[1]);
                }
                else {
                    subgraph.addEdge(pathwayTransitionEdges[0]);
                }
            }
        }
        return toDot(graph);
    };
    getTransitionNode = (transition) => {
        const id = this.generateId(IDGeneratorPrefix_TransitionPoint, transition._id ?? newItemId);
        const className = _className(this.transitionNodeClass, this.props.disabled && 'disabled');
        return new Node(id, {
            id: id,
            shape: 'circle',
            width: 0.2,
            label: '',
            style: 'filled',
            fillcolor: '#ffffff',
            class: className,
            tooltip: transition.label?.trim() || 'Transition',
        });
    };
    getPathwayStateNode = (_pathwayState, capitalize) => {
        const selected = _pathwayState._id === this.state.editablePathwayState?.item._id;
        const selectedTransition = !!this.state.editablePathwayTransition;
        const pathwayState = selected ? this.state.editablePathwayState?.item : _pathwayState;
        const className = _className(this.nodeClass, selected && !selectedTransition && 'selected', this.props.disabled && 'disabled');
        const label = capitalize ? capitalizeFirstLetter(pathwayState.name ?? '') : pathwayState.name;
        const id = this.generateId(IDGeneratorPrefix_State, pathwayState._id ?? newItemId);
        return new Node(id, {
            id: id,
            shape: 'rect',
            style: 'filled',
            // label: this.createHtmlLabel(pathwayState.name!), //todo @hpcc-js/wasm messed this up, they made it stop act as wrap-multiline and it shows ids instead of labels, issue opened, dunno if they'll follow up https://github.com/magjac/d3-graphviz/issues/319
            label: label,
            fillcolor: '#ffffff',
            fontsize: 13,
            fontcolor: '#333333',
            tooltip: _pathwayState.name ?? 'New State',
            class: className,
        });
    };
    getPathwayTransitionNode = (_pathwayTransition, stateNodes, midNode) => {
        const selected = _pathwayTransition._id === this.state.editablePathwayTransition?.item._id;
        const pathwayTransition = selected ? this.state.editablePathwayTransition.item : _pathwayTransition;
        const sourceId = pathwayTransition.sourceId;
        const targetId = pathwayTransition.targetId;
        const className = _className(this.edgeClass, selected && 'selected');
        //Could not find a source or target id
        if (!sourceId || !targetId) {
            //If no id (new transition) that's okay, return
            if (!pathwayTransition._id)
                return;
            //id exists, this is a problem
            throw new MUSTNeverHappenException(`Trying to render a pathway transition ${pathwayTransition._id} with no source or target`);
        }
        const origin = stateNodes[sourceId];
        if (!origin)
            throw new MUSTNeverHappenException(`Could not find an origin with id ${pathwayTransition.sourceId}`);
        const target = stateNodes[targetId];
        if (!target)
            throw new MUSTNeverHappenException(`Could not find an target with id ${pathwayTransition.targetId}`);
        if (this.state.transitionMidPoints) {
            const startId = this.generateId(IDGeneratorPrefix_Transition, pathwayTransition._id ?? newItemId, IDGeneratorSuffix_Start);
            const endId = this.generateId(IDGeneratorPrefix_Transition, pathwayTransition._id ?? newItemId, IDGeneratorSuffix_End);
            return [new Edge([origin, midNode], {
                    class: className,
                    arrowhead: 'none',
                    id: startId
                }), new Edge([midNode, target], { class: className, id: endId })];
        }
        const id = this.generateId(IDGeneratorPrefix_Transition, pathwayTransition._id ?? newItemId);
        return [new Edge([origin, target], { class: className, id: id })];
    };
    // ######################## Logic ########################
    injectLoader = (nodeId) => {
        const a = document.getElementById(`${IDGeneratorPrefix_State}${nodeId}`).querySelector('a');
        const rect = document.getElementById(`${IDGeneratorPrefix_State}${nodeId}`).querySelector('rect');
        const graphNode = getGraphLoader(rect);
        a.appendChild(graphNode);
    };
    removeLoader = () => {
        document.querySelectorAll('.graph-loader').forEach(node => node.remove());
    };
    convertPolygonsToRects = () => {
        const nodes = Array.from(document.getElementsByClassName(this.nodeClass));
        nodes.forEach(node => {
            const polygon = node.querySelector('polygon');
            if (!polygon)
                return;
            const rectData = this.getRectDataFromPolygon(polygon);
            const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
            rect.setAttribute('width', rectData.width);
            rect.setAttribute('height', rectData.height);
            rect.setAttribute('rx', '4');
            rect.setAttribute('ry', '4');
            rect.setAttribute('fill', '#fff');
            rect.setAttribute('x', rectData.x);
            rect.setAttribute('y', rectData.y);
            rect.setAttribute('stroke', '#4f4f4f');
            rect.setAttribute('stroke-width', '2');
            polygon.replaceWith(rect);
        });
    };
    addCheckMarksToTransitionNodes = () => {
        selectAll(`.${this.transitionNodeClass}`)
            .each(function () {
            const node = select(this);
            const a = node.select('g').select('a');
            const ellipse = a.select('ellipse');
            const posX = ellipse.attr('cx');
            const posY = ellipse.attr('cy');
            a
                .append('foreignObject')
                .attr('x', Number(posX) - 8)
                .attr('y', Number(posY) - 8)
                .attr('width', 16)
                .attr('height', 16)
                .html(`
				<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="transition-node-check">
    			<circle cx="8" cy="8" r="8" fill="#42BCC7" data-no-stroke="true"/>
    			<path d="M12 8L6 3V13L12 8Z" fill="white" data-no-stroke="true"/>
				</svg>
				<svg data-variant="icon-with-background" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" class="transition-node-fail">
    			<circle class="icon-background" data-no-stroke="true" cx="9" cy="9" r="9" fill="#E6E5FF"/>
    			<g class="inner-vectors">
        	<path d="M11.9186 5.88965L6.08249 11.7257" stroke="#9B9AC8" stroke-width="1.3" stroke-miterlimit="10"
              stroke-linecap="round" data-no-fill="true"/>
        	<path d="M6.08252 5.89001L11.9186 11.7261" stroke="#9B9AC8" stroke-width="1.3" stroke-miterlimit="10"
              stroke-linecap="round" data-no-fill="true"/>
    			</g>
				</svg>
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="transition-node-insufficient">
        	<path d="M5,5 A3 3 270 1 1 8,9 L8,10.5 M8,13 l0,0" stroke="#fff" stroke-miterlimit="10"
        stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"
        fill="none" data-no-fill="true"/>
        </svg>
      `);
        });
    };
    updateTransitionsParams = (nextState) => {
        if (!nextState.focusedTransitions)
            return;
        const nodes = [...Array.from(document.getElementsByClassName(this.edgeClass)), ...Array.from(document.getElementsByClassName(this.transitionNodeClass))];
        nodes.forEach(node => {
            const splitId = node.id.split('-');
            const transitionId = splitId[1] !== 'point' ? splitId[1] : splitId[2];
            const transitionData = nextState.focusedTransitions[transitionId];
            if (transitionData?.params.goto)
                node.classList.add('goto');
            else
                node.classList.remove('goto');
            if (transitionData?.params.missingInfo)
                node.classList.add('missing-info');
            else
                node.classList.remove('missing-info');
            if (transitionData?.params.resolvedFalse) {
                node.classList.add('resolved-false');
            }
            else {
                node.classList.remove('resolved-false');
            }
            if (transitionData?.params.focused) {
                node.classList.add('focused');
            }
            else {
                node.classList.remove('focused');
            }
            if (transitionData?.params.visited) {
                node.classList.add('visited');
            }
            else {
                node.classList.remove('visited');
            }
        });
    };
    updateStateParams = (nextState) => {
        if (!nextState.focusedStates)
            return;
        const nodes = Array.from(document.getElementsByClassName(this.nodeClass));
        nodes.forEach(node => {
            const splitId = node.id.split('-');
            const stateId = splitId[1];
            const stateData = nextState.focusedStates[stateId];
            if (stateData?.params.visited) {
                node.classList.add('visited');
            }
            else {
                node.classList.remove('visited');
            }
        });
    };
    /**
     * todo changed from private to public temporarily since we hope to return to this solution once the issue will be fixed.
     * @param text
     * @param maxWidth
     */
    createHtmlLabel = (text, maxWidth = RectMaxWidth) => {
        const textWidth = this.measurementCanvas.measureText(text).width + 25;
        const width = Math.min(Math.max(textWidth, 100), maxWidth);
        const wrappedText = text.replace(new RegExp(`(.{1,${Math.floor(width / 6)}})(\\s|$)`, 'g'), '$1<br/>');
        return `<
			<table border="0" cellborder="0" cellpadding="0" cellspacing="0" width="${width}">
			  <tr>
				<td style="padding: 5px 10px; min-width: 100px;" width="${width}" fixedsize="true" valign="middle" align="center">${wrappedText}</td>
			  </tr>
			</table>
  		>`;
    };
    getRectDataFromPolygon = (polygon) => {
        const points = _values(polygon.points);
        const minX = Math.min(...points.map(point => point.x));
        const maxX = Math.max(...points.map(point => point.x));
        const minY = Math.min(...points.map(point => point.y));
        const maxY = Math.max(...points.map(point => point.y));
        const data = {
            x: String(minX),
            y: String(minY),
            width: String(maxX - minX),
            height: String(maxY - minY),
        };
        return data;
    };
    dispatchStateSelected = (nodeId) => {
        if (nodeId === newItemId)
            return;
        dispatch_PathwayStateSelected.dispatchUI(nodeId);
    };
    setStateOnRightClick = () => {
        const states = select(this.graphContainerRef.current).selectAll(`.${this.nodeClass}`);
        states.on('dblclick', (e) => {
            const nodeId = e.currentTarget.id.replace(IDGeneratorPrefix_State, '');
            if (this.props.disabled || this.state.loadingStateId || this.state.editablePathwayState?.item._id === nodeId)
                return;
            if (this.props.onStateDoubleClicked) {
                const result = this.props.onStateDoubleClicked(nodeId);
                if (isPromise(result))
                    return this.setState({ loadingStateId: nodeId }, async () => {
                        //@ts-ignore
                        delete this.state.loadingStateId;
                        this.dispatchStateSelected(nodeId);
                    });
            }
            this.dispatchStateSelected(nodeId);
        });
    };
    setStateOnClick = () => {
        const states = select(this.graphContainerRef.current).selectAll(`.${this.nodeClass}`);
        states.on('click', (e) => {
            const nodeId = e.currentTarget.id.replace(IDGeneratorPrefix_State, '');
            if (this.props.disabled || this.state.loadingStateId || this.state.editablePathwayState?.item._id === nodeId)
                return;
            if (this.props.onStateClicked) {
                const result = this.props.onStateClicked(nodeId);
                if (isPromise(result))
                    return this.setState({ loadingStateId: nodeId }, async () => {
                        //@ts-ignore
                        delete this.state.loadingStateId;
                        this.dispatchStateSelected(nodeId);
                    });
            }
            this.dispatchStateSelected(nodeId);
        });
    };
    setTransitionOnClick = () => {
        const transitions = select(this.graphContainerRef.current).selectAll(`.${this.transitionNodeClass}`);
        transitions.on('click', (e) => {
            if (this.props.disabled)
                return;
            const transitionId = e.currentTarget.id
                .replace(IDGeneratorPrefix_TransitionPoint, '');
            if (transitionId === newItemId)
                return;
            dispatch_PathwayTransitionSelected.dispatchAll(transitionId);
        });
    };
    // ######################## Render ########################
    render() {
        return _jsxs("div", { className: 'pathway-graph-view', ref: this.graphContainerRef, children: [this.renderFocusUsers(), this.renderTransitionAB()] });
    }
    renderGraph = () => {
        if (!Object.keys(this.state).length)
            return;
        const graphString = this.getGraphString();
        const rect = this.graphContainerRef.current.getBoundingClientRect();
        const subGraph = document.getElementById('graph0');
        if (subGraph)
            subGraph.innerHTML = '';
        graphviz(this.graphContainerRef.current, {
            useWorker: false,
            zoomScaleExtent: this.props.zoomScale ?? [0.5, 2], // control zoom scale by context
            width: rect.width,
            height: rect.height,
            ...this.props.graphvizOptions // add external options from outside
        })
            .renderDot(graphString)
            .on('renderEnd', () => {
            try {
                this.convertPolygonsToRects();
                this.addCheckMarksToTransitionNodes();
                this.setStateOnClick();
                this.setStateOnRightClick();
                this.setTransitionOnClick();
            }
            catch (err) {
                this.logError('Failed graph renderEnd process');
            }
        });
    };
    renderFocusUsers = () => {
        if (this.props.hideFocusEntity)
            return '';
        return _jsx(Component_FocusedEntityRef, { focusedEntities: [{ dbKey: DBDef_Pathway.dbKey, itemId: this.state.pathwayId }] });
    };
    renderTransitionAB = () => {
        if (this.props.disabled || this.props.hideTransitionAB)
            return '';
        return _jsx(TS_Checkbox, { className: 'pathway-graph-view__a-b', checked: this.state.transitionMidPoints, onCheck: () => this.setState({ transitionMidPoints: !this.state.transitionMidPoints }), children: "Show Transitions" });
    };
}
//# sourceMappingURL=Pathway_GraphView.js.map